import { useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'

import { ResetIcon } from '@/components/icons/ResetIcon'
import { ExplosionsState, useCADPageStore } from '@/state/cad'
import { HighlighterIcon } from '@/components/icons/HighlighterIcon'
import { COLOR_OPTIONS } from '@/constants'
import { GLTFObject } from '@/lib/cad/GLTFObject'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { ControlButton } from '../ControlPanel/ControlButton'
import { CaretDownIcon } from '@radix-ui/react-icons'
import { useHighlightStep } from '../ControlPanel/hooks/useHighlightStep'

export const StepHighlighterButton = ({ gltf }: { gltf: GLTFObject }) => {
  const { steps } = useOperationSteps()
  const highlightStep = useHighlightStep(gltf)
  const [menuOpen, setMenuOpen] = useState(false)
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const operationStep = useCADPageStore((state) => state.operationStep)
  const wandSelected = useCADPageStore((state) => state.wandSelected)
  const highlighting = useCADPageStore((state) => state.highlighting)
  const highlightColor = useCADPageStore((state) => state.highlightColor)
  const exploding = useCADPageStore(
    (state) => state.explosionsState === 'ENABLED',
  )
  const resetHighlightedParts = useCADPageStore(
    (state) => state.resetHighlightedParts,
  )

  const step = steps.find((s) => s.id === operationStep?.stepId)

  const activeColor = highlightColor ?? 0x0080ff
  const activeColorHex = `#${activeColor.toString(16).padStart(6, '0').toUpperCase()}`

  return (
    <div data-testid="step-highlighter-button">
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <ControlButton
            asChild
            icon={
              <div className="flex flex-row items-center">
                <HighlighterIcon
                  className="w-5 h-5"
                  color={highlighting ? activeColorHex : '#424242'}
                />
                <CaretDownIcon />
              </div>
            }
            title="Highlighter"
            isActive={Boolean(step) && highlighting}
            onClick={() => {
              if (exploding) {
                setCadPageState({
                  selectedParts: [],
                  explosionsState: ExplosionsState.DISABLED,
                  isEditModeActive: false,
                })
              }

              if (wandSelected !== null) {
                setCadPageState({
                  isEditModeActive: false,
                  wandSelected: null,
                })
              }

              if (menuOpen) {
                highlightStep(step, 0x0080ff)
                setCadPageState({ highlighting: true })
              } else if (highlighting) {
                resetHighlightedParts()
                setCadPageState({ highlighting: false, highlightColor: null })
              }
            }}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" className="flex">
          <DropdownMenuItem data-testid={`step-highlighter-color-option-reset`}>
            <ControlButton
              className="p-1 w-12 h-12 rounded-md"
              icon={<ResetIcon className="w-5 h-5" />}
              title={`Reset`}
              onClick={(e) => {
                e.stopPropagation()
                resetHighlightedParts()
                setCadPageState({ highlighting: false, highlightColor: null })
                setMenuOpen(false)
              }}
            />
          </DropdownMenuItem>
          {COLOR_OPTIONS.map((colorOption) => {
            const colorValue = `#${colorOption.value.toString(16).padStart(6, '0').toUpperCase()}`
            return (
              <DropdownMenuItem
                data-testid={`step-highlighter-color-option-${colorOption.name}`}
                key={colorOption.value}
                onClick={(e) => {
                  e.stopPropagation()
                  highlightStep(step, colorOption.value)
                  setMenuOpen(false)
                }}
                className="p-0 cursor-pointer"
              >
                <ControlButton
                  className="p-1 w-12 h-12 rounded-md"
                  icon={
                    <HighlighterIcon className="w-5 h-5" color={colorValue} />
                  }
                  title={`${colorOption.name}`}
                />
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
